import CustomDurationPicker from "@components/FormFields/CustomDurationPicker";
import CustomSelectMenu from "@components/FormFields/CustomSelectMenu";
import QueryFetchStatePage from "@components/QueryFetchStatePage";
import { Button } from "@components/ui/button";
import { CircularProgressIndicator } from "@components/shared/CircularProgressIndicator";
import { useAssets } from "@hooks/useAssets";
import { usePublicAssets } from "@hooks/usePublicAssets";
import { ScreenPlayingOptionType } from "@hooks/useScreens/types";
import generateAssetUrl from "@utils/generateAssetUrl";
import { useEffect, useState } from "react";
import { IoReload } from "react-icons/io5";
import { MdDone } from "react-icons/md";
import { cn } from "@utils/index";

function SelectImages({
  selected = [],
  onSelected,
  duration,
  setDuration,
  imageFit,
  setImageFit,
  listOnly = false,
}: {
  duration: number;
  setDuration: (val: number) => void;
  imageFit: string | null;
  setImageFit: (val: string | null) => void;
  selected?: ScreenPlayingOptionType[];
  onSelected: (assets: ScreenPlayingOptionType[]) => void;
  listOnly?: boolean;
}) {
  const {
    assets,
    assetsFetchStatus,
    getAssets,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useAssets();
  const {
    assets: publicAssets,
    assetsFetchStatus: publicAssetsFetchStatus,
    getAssets: getPublicAssets,
    hasNextPage: hasNextPublicPage,
    fetchNextPage: fetchNextPublicPage,
    isFetchingNextPage: isFetchingNextPublicPage,
  } = usePublicAssets();

  const [publicAssetsSelected, setPublicAssetsSelected] = useState(false);

  useEffect(() => {
    if (assets.length === 0) {
      setPublicAssetsSelected(true);
    }
  }, [assets]);

  return (
    <QueryFetchStatePage
      queryFetchStatus={assetsFetchStatus}
      onRetry={getAssets}
    >
      <QueryFetchStatePage
        queryFetchStatus={publicAssetsFetchStatus}
        onRetry={getPublicAssets}
      >
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 sm:gap-2">
          <div className="flex w-full items-center justify-between">
            {
              !listOnly && (
                <div className="flex gap-2">
                  {assets.length > 0 &&
                    tab("Your Assets", !publicAssetsSelected, () => {
                      setPublicAssetsSelected(false);
                    })}
                  {tab("Browse Free Assets", publicAssetsSelected, () => {
                    setPublicAssetsSelected(true);
                  })}
                </div>
              )
            }

            <div className="flex gap-2">
              {selected?.some(s => s.type === "asset") && (
                <div className="w-36">
                  <CustomSelectMenu
                    label="Image Fit"
                    options={["fill", "contain", "cover", "none", "scale-down"]}
                    onChange={setImageFit}
                    selected={imageFit}
                    optionRender={(e) => (
                      <p className="truncate capitalize">{e}</p>
                    )}
                    selectedRender={(e) => (
                      <p className="truncate capitalize"> {e}</p>
                    )}
                  />
                </div>
              )}
              {selected?.isLongerThan(1) && (
                <CustomDurationPicker
                  key={"duration_picker"}
                  selectedDurationInSeconds={duration}
                  onDurationChanged={setDuration}
                  label="Each image display duration"
                />
              )}
            </div>
          </div>
          <div className="h-full w-full flex-1 flex-col items-center justify-center gap-4 sm:gap-2 overflow-y-auto">
            <div className="grid grid-cols-2 gap-4 sm:gap-2 lg:grid-cols-3 xl:grid-cols-4">
              {(listOnly ? [...assets, ...publicAssets] : publicAssetsSelected ? publicAssets : assets).map((asset) => {
                const _isSelected = selected?.some(s => s.id === asset.id)
                return (
                  <button
                    key={asset.id}
                    className={cn(
                      "relative h-32 overflow-clip rounded-md border-2 bg-lightCard shadow-sm dark:bg-darkCard",
                      _isSelected
                        ? "border-accentColor dark:border-darkAccentColor"
                        : "border-borderColor dark:border-darkBorderColor",
                      listOnly && "h-12"
                    )}
                    onClick={() => {
                      if (_isSelected) {
                        onSelected(selected?.filter(s => s.id !== asset.id) ?? []);
                      } else {
                        onSelected([...selected, { ...asset, type: "asset" }]);
                      }
                    }}
                  >
                    <img
                      className="h-full w-full object-cover"
                      alt={asset.name ?? ""}
                      src={generateAssetUrl(asset.id, "small")}
                    />

                    <div
                      className={cn(
                        "absolute bottom-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition-all",
                        _isSelected ? "scale-100" : "scale-0"
                      )}
                    >
                      <MdDone />
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex w-full items-center justify-center pt-4">
              {!publicAssetsSelected && hasNextPage && (
                <Button
                  size="sm"
                  disabled={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                >
                  {isFetchingNextPage ? (
                    <CircularProgressIndicator />
                  ) : (
                    <IoReload />
                  )}
                  Load More
                </Button>
              )}
              {publicAssetsSelected && hasNextPublicPage && (
                <Button
                  size="sm"
                  disabled={isFetchingNextPublicPage}
                  onClick={() => fetchNextPublicPage()}
                >
                  {isFetchingNextPublicPage ? (
                    <CircularProgressIndicator />
                  ) : (
                    <IoReload />
                  )}
                  Load More
                </Button>
              )}
            </div>
          </div>
        </div>
      </QueryFetchStatePage>
    </QueryFetchStatePage>
  );

  function tab(title: string, selected: boolean, onClick: () => void) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`mr-2 flex cursor-pointer flex-row items-center gap-2 rounded-t-lg border-b-2 border-transparent p-2 transition-all ${selected
          ? "!border-accentColor text-accentColor dark:!border-darkAccentColor dark:text-darkAccentColor"
          : "hover:border-accentColor hover:border-opacity-50 hover:text-accentColor dark:hover:border-darkAccentColor dark:hover:border-opacity-50 dark:hover:text-darkAccentColor"
          }`}
      >
        {title}
      </button>
    );
  }
}

export default SelectImages;
